import React, { useEffect, useState } from 'react';

import { PdfRenderer } from '@/components/templates-v2/document-generation/pdf/renderer/PdfFormRenderer';
import { useMatter } from '@/hooks/useMatter';
import { useGetBlobDetailQuery } from '@/services/api/blobService';

import { normalizeToPdf } from '../package-edit/utils';
import { MemoizedIframePreview } from './MemoizedIframePreview';

interface BlobDocumentPreviewProps {
  blobId: string;
  fitPage?: boolean;
  maxPages?: number;
  renderHeight?: number;
}

const PREVIEW_WIDTH = 700;

/**
 * Component for blob-based documents (like client ID)
 * Fetches the blob, normalizes it to PDF if needed, and renders it
 * Uses a fixed width to prevent flickering on resize
 */
export const BlobDocumentPreview: React.FC<BlobDocumentPreviewProps> = ({
  blobId,
  fitPage = false,
  maxPages,
  renderHeight,
}) => {
  const { matterId } = useMatter();
  const { data: blobDetail, isLoading } = useGetBlobDetailQuery(
    { matterId, blobId },
    { skip: !blobId || !matterId },
  );
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const isPdfBlob = Boolean(
    blobDetail?.file_extension?.toLowerCase() === 'pdf' ||
      blobDetail?.file_mime_type?.includes('pdf'),
  );
  const previewUrl = isPdfBlob
    ? blobDetail?.download_url
    : blobDetail?.pdf_preview_url;

  useEffect(() => {
    const fetchAndNormalize = async () => {
      if (previewUrl || !blobDetail?.download_url) {
        setPdfBlob(null);
        return;
      }

      if (blobDetail.download_url) {
        const response = await fetch(blobDetail.download_url);
        const blob = await response.blob();
        const normalizedPdf = await normalizeToPdf(blob);
        setPdfBlob(normalizedPdf);
      }
    };
    fetchAndNormalize();
  }, [blobDetail?.download_url, previewUrl]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (previewUrl) {
    return (
      <div className="h-full w-full">
        <MemoizedIframePreview
          url={previewUrl}
          title={blobDetail?.name || blobDetail?.original_filename || 'Document preview'}
        />
      </div>
    );
  }

  if (!pdfBlob) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-sm text-gray-500">Document not found</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full p-4">
      <PdfRenderer
        file={pdfBlob}
        renderWidth={fitPage ? undefined : PREVIEW_WIDTH}
        renderHeight={fitPage ? renderHeight : undefined}
        maxPages={maxPages}
      />
    </div>
  );
};
